import React, { useEffect, useState, useMemo, useRef } from "react";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { PageTitle, EmptyState, Badge } from "@/components/ui-kit";
import InspireBanner from "@/components/InspireBanner";
import SchoolFilter, { useSchools } from "@/components/SchoolFilter";
import { VOL_TYPE } from "@/lib/constants";
import { downloadFile, uploadFile } from "@/lib/download";
import { Users, Plus, Search, Pencil, Trash2, FileDown, FileUp, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { playSuccess } from "@/lib/sound";

const empty = { name: "", type: "student", grade: "أول ثانوي", job_title: "", hours: 0, external_hours: 0, certificate_image: "", school_id: "" };
const GRADES = ["أول ثانوي", "ثاني ثانوي", "ثالث ثانوي"];
const F = ({ label, children }) => (<div><label className="text-xs text-stone-500 mb-1 block">{label}</label>{children}</div>);
const inputCls = "w-full px-3 py-2 rounded-lg bg-amber-50 border border-amber-400/30 text-stone-800 text-sm focus:outline-none focus:border-amber-400";

export default function Volunteers({ fixedType, fixedGrade }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const schools = useSchools();
  const pageTitle = fixedGrade ? `طلاب ${fixedGrade}` : fixedType === "student" ? "الطلاب المتطوعون" : fixedType === "staff" ? "الموظفون المتطوعون" : "المتطوعون";
  const pageSub = fixedGrade ? `سجل الطلاب المتطوعين — ${fixedGrade}` : fixedType === "student" ? "سجل الطلاب المتطوعين" : fixedType === "staff" ? "سجل الموظفين المتطوعين" : "سجل المتطوعين من الطلاب والموظفين";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [typeF, setTypeF] = useState("");
  const [schoolF, setSchoolF] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const fileRef = useRef();

  const load = () => {
    const qp = isAdmin && schoolF ? `?school_id=${schoolF}` : "";
    api.get(`/volunteers${qp}`).then((r) => { setItems(r.data); setSelected(new Set()); }).catch(() => {});
  };
  useEffect(load, [schoolF, isAdmin]);

  const filtered = useMemo(() => items.filter((v) =>
    (!q || v.name?.includes(q)) && (!typeF || v.type === typeF) && (!fixedType || v.type === fixedType)
    && (!fixedGrade || (v.type === "student" && v.grade === fixedGrade))
  ), [items, q, typeF, fixedType, fixedGrade]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onCert = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 3 * 1024 * 1024) return toast.error("حجم الصورة يجب أن يكون أقل من 3 ميجا");
    const r = new FileReader(); r.onload = () => set("certificate_image", r.result); r.readAsDataURL(f);
  };
  const openNew = () => { setForm({ ...empty, type: fixedType || "student", grade: fixedGrade || "أول ثانوي" }); setEditId(null); setOpen(true); };
  const openEdit = (v) => { setForm({ ...empty, ...v }); setEditId(v.id); setOpen(true); };

  const save = async () => {
    if (!form.name) return toast.error("الاسم مطلوب");
    if (isAdmin && !editId && !form.school_id) return toast.error("اختر المدرسة");
    try {
      const payload = { ...form };
      delete payload.hours;
      if (editId) await api.put(`/volunteers/${editId}`, payload);
      else await api.post("/volunteers", payload);
      playSuccess();
      toast.success(editId ? "تم التحديث" : "تمت الإضافة");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async () => {
    try { await api.delete(`/volunteers/${delId}`); toast.success("تم الحذف"); setDelId(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const toggleSelect = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = filtered.length > 0 && filtered.every((v) => selected.has(v.id));
  const selectedVisible = filtered.filter((v) => selected.has(v.id));
  const toggleAll = () => setSelected((s) => {
    if (filtered.length > 0 && filtered.every((v) => s.has(v.id))) { const n = new Set(s); filtered.forEach((v) => n.delete(v.id)); return n; }
    return new Set([...s, ...filtered.map((v) => v.id)]);
  });
  const bulkRemove = async () => {
    const ids = selectedVisible.map((v) => v.id);
    if (!ids.length) { setBulkConfirm(false); return; }
    try {
      const res = await api.post("/volunteers/bulk-delete", { ids });
      toast.success(`تم حذف ${res.data.deleted} متطوع`);
      setBulkConfirm(false); setSelected(new Set()); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doExport = async () => {
    const params = new URLSearchParams();
    if (isAdmin && schoolF) params.set("school_id", schoolF);
    if (fixedGrade) params.set("grade", fixedGrade);
    const qs = params.toString();
    try { await downloadFile(`/volunteers/export${qs ? `?${qs}` : ""}`, fixedGrade ? "students.xlsx" : "volunteers.xlsx"); }
    catch { toast.error("تعذر التصدير"); }
  };

  const doTemplate = async () => {
    const qs = fixedGrade ? `?grade=${encodeURIComponent(fixedGrade)}` : fixedType ? `?vtype=${encodeURIComponent(fixedType)}` : "";
    try { await downloadFile(`/volunteers/import/template${qs}`, fixedGrade ? "students_template.xlsx" : "volunteers_template.xlsx"); }
    catch { toast.error("تعذر تحميل القالب"); }
  };

  const doImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (isAdmin && !schoolF) { toast.error("اختر مدرسة أولاً للاستيراد"); return; }
    try {
      const params = new URLSearchParams();
      if (isAdmin) params.set("school_id", schoolF);
      if (fixedGrade) params.set("grade", fixedGrade);
      if (fixedType) params.set("vtype", fixedType);
      const qs = params.toString();
      const path = `/volunteers/import${qs ? `?${qs}` : ""}`;
      const res = await uploadFile(path, file);
      const movedTxt = res.moved ? ` ونقل ${res.moved} إلى ${fixedGrade || "المرحلة"}` : "";
      toast.success(`تم استيراد ${res.added}${movedTxt} ${fixedGrade ? "طالب" : "متطوع"}، وتخطي ${res.skipped} مكرر`);
      load();
    } catch (err) { toast.error(err.message || "تعذر الاستيراد"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <InspireBanner />
      <PageTitle title={pageTitle} subtitle={pageSub} icon={fixedType === "student" ? undefined : Users}
        actions={<>
          <button data-testid="template-vol-btn" onClick={doTemplate} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileText size={17} /> قالب Excel</button>
          <button data-testid="export-vol-btn" onClick={doExport} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileDown size={17} /> تصدير Excel</button>
          <button data-testid="import-vol-btn" onClick={() => fileRef.current?.click()} className="glass glass-hover px-3 py-2 rounded-xl flex items-center gap-2 text-stone-800 text-sm"><FileUp size={17} /> استيراد Excel</button>
          <input ref={fileRef} type="file" accept=".xlsx" hidden onChange={doImport} />
          {!isAdmin && <button data-testid="add-vol-btn" onClick={openNew} className="btn-gold px-4 py-2 rounded-xl flex items-center gap-2"><Plus size={18} /> متطوع جديد</button>}
        </>}
      />

      <div className="glass rounded-2xl p-4 mb-5 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400" size={18} />
          <input data-testid="vol-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="بحث بالاسم..." className={`${inputCls} pr-10`} />
        </div>
        {!fixedType && (
          <select value={typeF} onChange={(e) => setTypeF(e.target.value)} className={inputCls + " max-w-[150px]"}>
            <option value="">الكل</option>
            <option value="student">طالب</option>
            <option value="staff">موظف</option>
          </select>
        )}
        {isAdmin && <SchoolFilter value={schoolF} onChange={setSchoolF} schools={schools} />}
        {!isAdmin && selectedVisible.length > 0 && (
          <button data-testid="bulk-delete-btn" onClick={() => setBulkConfirm(true)} className="px-4 py-2 rounded-xl bg-red-600 text-white hover:bg-red-700 flex items-center gap-2 text-sm font-medium">
            <Trash2 size={16} /> حذف المحدد ({selectedVisible.length})
          </button>
        )}
      </div>

      {!filtered.length ? <EmptyState text="لا يوجد متطوعون" icon={Users} /> : (
        <div className="glass rounded-2xl p-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-stone-500 text-right border-b border-amber-400/15">
                {!isAdmin && <th className="py-3 px-2 w-10"><input type="checkbox" data-testid="select-all-vol" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-amber-500 cursor-pointer" /></th>}
                <th className="py-3 px-2 font-medium">الاسم</th>
                <th className="py-3 px-2 font-medium">النوع</th>
                <th className="py-3 px-2 font-medium">الصف/المسمى</th>
                <th className="py-3 px-2 font-medium">الساعات</th>
                <th className="py-3 px-2 font-medium">ساعات للمتابعة</th>
                <th className="py-3 px-2 font-medium">الشهادة</th>
                {!isAdmin && <th className="py-3 px-2 font-medium">إجراءات</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map((v) => (
                <tr key={v.id} className={`border-b border-amber-400/10 transition ${selected.has(v.id) ? "bg-amber-100/70" : "hover:bg-amber-100/50"}`}>
                  {!isAdmin && <td className="py-3 px-2"><input type="checkbox" data-testid={`select-vol-${v.id}`} checked={selected.has(v.id)} onChange={() => toggleSelect(v.id)} className="w-4 h-4 accent-amber-500 cursor-pointer" /></td>}
                  <td className="py-3 px-2 text-stone-800 font-medium">{v.name}</td>
                  <td className="py-3 px-2"><Badge className={v.type === "student" ? "bg-blue-500/15 text-blue-700 border-blue-400/30" : "bg-amber-500/15 text-amber-700 border-amber-400/30"}>{VOL_TYPE[v.type]}</Badge></td>
                  <td className="py-3 px-2 text-stone-600">{v.type === "student" ? v.grade : v.job_title || "-"}</td>
                  <td className="py-3 px-2 text-amber-600 font-semibold">{v.total_hours}</td>
                  <td className="py-3 px-2 text-sky-700" title="ساعات منصة التطوع للمتابعة — لا تدخل في المؤشر">{v.external_hours || 0}</td>
                  <td className="py-3 px-2">{v.certificate_image ? <a href={v.certificate_image} target="_blank" rel="noreferrer" className="text-emerald-600 underline text-xs">عرض ✓</a> : <span className="text-red-500 text-xs">مفقودة</span>}</td>
                  {!isAdmin && <td className="py-3 px-2">
                    <div className="flex gap-1">
                      <button data-testid={`edit-vol-${v.id}`} onClick={() => openEdit(v)} className="p-1.5 rounded-lg hover:bg-amber-500/20 text-amber-600"><Pencil size={16} /></button>
                      <button data-testid={`del-vol-${v.id}`} onClick={() => setDelId(v.id)} className="p-1.5 rounded-lg hover:bg-red-500/20 text-red-700"><Trash2 size={16} /></button>
                    </div>
                  </td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-amber-400/30 text-stone-800 max-w-lg" dir="rtl">
          <DialogHeader><DialogTitle className="gold-text font-head">{editId ? "تعديل متطوع" : "متطوع جديد"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {isAdmin && !editId && <F label="المدرسة"><select value={form.school_id} onChange={(e) => set("school_id", e.target.value)} className={inputCls}><option value="">اختر</option>{schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></F>}
            <F label="الاسم الكامل"><input data-testid="vol-name" value={form.name} onChange={(e) => set("name", e.target.value)} className={inputCls} /></F>
            <F label="النوع"><select value={form.type} disabled={!!fixedType} onChange={(e) => set("type", e.target.value)} className={inputCls}><option value="student">طالب</option><option value="staff">موظف</option></select></F>
            {form.type === "student"
              ? <F label="المرحلة الدراسية"><select data-testid="vol-grade" value={form.grade} disabled={!!fixedGrade} onChange={(e) => set("grade", e.target.value)} className={inputCls}>{GRADES.map((g) => <option key={g} value={g}>{g}</option>)}</select></F>
              : <F label="المسمى الوظيفي"><input value={form.job_title} onChange={(e) => set("job_title", e.target.value)} className={inputCls} /></F>}
            <F label="عدد ساعات التطوع"><div data-testid="vol-hours-locked" className="w-full px-3 py-2 rounded-lg bg-stone-100 border border-amber-400/30 text-stone-500 text-sm">تُحتسب تلقائياً من مشاركات الفرص</div></F>
            <F label="ساعات للمتابعة (منصة التطوع)"><input data-testid="vol-external-hours" type="number" min="0" step="0.5" value={form.external_hours} onChange={(e) => set("external_hours", e.target.value)} className={inputCls} placeholder="ساعات سابقة للمتابعة فقط — لا تدخل المؤشر" /></F>
            <div className="sm:col-span-2">
              <F label="صورة شهادة المتطوع بالساعات من منصة التطوع">
                <input data-testid="vol-cert" type="file" accept="image/*" onChange={onCert} className={inputCls} />
                {form.certificate_image && <img src={form.certificate_image} alt="شهادة" className="mt-2 w-full max-h-52 object-contain rounded-lg border border-amber-400/30" />}
              </F>
            </div>
          </div>
          <DialogFooter><button data-testid="save-vol-btn" onClick={save} className="btn-gold px-6 py-2 rounded-xl">حفظ</button></DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={() => setDelId(null)}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">تأكيد الحذف</AlertDialogTitle><AlertDialogDescription className="text-stone-500">سيتم حذف المتطوع وجميع مشاركاته.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700">حذف</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkConfirm} onOpenChange={setBulkConfirm}>
        <AlertDialogContent className="glass border-amber-400/30 text-stone-800" dir="rtl">
          <AlertDialogHeader><AlertDialogTitle className="text-stone-800">حذف مجموعة متطوعين</AlertDialogTitle><AlertDialogDescription className="text-stone-500">سيتم حذف {selectedVisible.length} متطوعاً وجميع مشاركاتهم. لا يمكن التراجع.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="bg-amber-50 border-amber-400/30 text-stone-800">إلغاء</AlertDialogCancel><AlertDialogAction data-testid="confirm-bulk-delete" onClick={bulkRemove} className="bg-red-600 hover:bg-red-700">حذف {selectedVisible.length}</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
